import React, { type ReactElement, useCallback, useEffect } from 'react'
import { Skeleton, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { ReactComponent as PrintIcon } from 'assets/print.svg'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import ThemeButton from 'common/ui/theme-button'
import { BUTTON_VARIANTS } from 'common/ui/theme-button/ThemeButton'
import diagram from 'assets/spmind_diagram.png'

import { getQuestionnaireResults } from '../questionnaires.service'
import { AICategory } from '../questionnaires.types'
import { VendorAIStatus } from '../../vendor.types'

import styles from './AIResults.module.scss'

const AiResults: React.FC = (): ReactElement => {
  const dispatch = useAppDispatch()
  const { palette } = useTheme()
  const { questionnaireResults, questionnaire } = useAppSelector((state) => state.questionnaires)
  const { vendor } = useAppSelector((state) => state.vendor)

  useEffect(() => {
    if (questionnaire?.id && vendor && vendor.aiStatus && vendor.aiStatus !== VendorAIStatus.rejected) {
      void dispatch(getQuestionnaireResults({ companyId: vendor.id, questionnaireId: questionnaire.id }))
    }
  }, [])

  const getClassname = useCallback(
    (categories: string[]) => {
      return questionnaireResults?.level.name && categories.includes(questionnaireResults?.level.name)
        ? styles.cellHighlight
        : ''
    },
    [questionnaireResults],
  )

  return palette ? (
    <div className={styles.container}>
      <div className={styles.printAction}>
        <ThemeButton
          variant={BUTTON_VARIANTS.outlined}
          onClick={() => {
            window.print()
          }}
          className={styles.button}
        >
          ODŠTAMPAJ <PrintIcon className={styles.printIcon} stroke="black" />
        </ThemeButton>
      </div>
      <div className={styles.section}>
        <Typography variant="title6" className={styles.sectionTitle}>
          Solution Provider Maturity Index (SPMIND)
        </Typography>
        <Typography variant="description3" className={styles.sectionDescription}>
          SPMIND omogućava pružaocima rešenja, koji razvijaju ili integrišu rešenja zasnovana na Veštačkoj
          Inteligenciji (AI), da budu prepoznati kao pouzdani pružaoci usluga za izgradnju i implementaciju
          takvih AI rešenja.
        </Typography>
        <table>
          <thead>
            <tr>
              <td></td>
              <th className={getClassname([AICategory.unaware])}>AI Unaware (Neupućeni u AI)</th>
              <th className={getClassname([AICategory.aware])}>AI Aware (Upoznati sa AI)</th>
              <th className={getClassname([AICategory.ready])}>AI Ready (Spremni za AI)</th>
              <th className={getClassname([AICategory.competent])}>AI Competent (Kompetentni u AI)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <b>Prosečan rezultat</b>
              </td>
              <td className={getClassname([AICategory.unaware])}>Manje od 2.5</td>
              <td className={getClassname([AICategory.aware])}>2.5 do 3.4</td>
              <td className={getClassname([AICategory.ready])}>3.5 do 4.5</td>
              <td className={getClassname([AICategory.competent])}>Više od 4.5</td>
            </tr>
            <tr>
              <td>
                <b>Opšte sposobnosti u ovladavanju AI Problemima</b>
              </td>
              <td className={getClassname([AICategory.unaware])}>
                Trenutni nivo razumevanja i sposobnosti u vezi sa AI problemima nije dovoljan za efikasno
                upravljanje i rešavanje izazova u AI projektima.
              </td>
              <td className={getClassname([AICategory.aware])}>
                Postoji osnovno razumevanje i sposobnost upravljanja AI problemima, ali sa primetnim
                nedostacima u pojedinim ključnim oblastima.
              </td>
              <td className={getClassname([AICategory.ready])}>
                Veoma dobro razumevanje i sposobnost upravljanja većinom aspekata AI problema, uz minimalne
                nedostatke u određenim specifičnim oblastima.
              </td>
              <td className={getClassname([AICategory.competent])}>
                Izuzetna sposobnost i stručnost u upravljanju i rešavanju svih aspekata AI problema, sa
                sveobuhvatnim pokrivanjem i razumevanjem svih dimenzija.
              </td>
            </tr>
            <tr>
              <td>
                <b>Opšte karakteristike</b>
              </td>
              <td className={getClassname([AICategory.unaware])}>
                Postoji mogućnost da se ne identifikuju sve potencijalne zamke i izazovi koji se javljaju u
                projektima veštačke inteligencije.
              </td>
              <td className={getClassname([AICategory.aware])}>
                Efikasno upravljanje većinom elemenata AI projekata, ali sa mogućim propustima u realizaciji
                nekih relativno jednostavnijih zadataka.
              </td>
              <td className={getClassname([AICategory.ready])}>
                Visok nivo ovladavanja većinom aspekata AI projekata, iako postoji mogućnost neuspeha u
                složenijim i zahtevnijim situacijama.
              </td>
              <td className={getClassname([AICategory.competent])}>
                Kompletna stručnost i sposobnost da se uspešno suoči sa svim izazovima AI projekata, uz
                očekivanje uspeha u svim situacijama i okolnostima.
              </td>
            </tr>
            <tr>
              <td>
                <b>Nivoi kvalifikacije pružaoca rešenja za</b>
              </td>
              <td className={getClassname([AICategory.unaware])}>
                Trenutni nivo sposobnosti i iskustva nije adekvatan da bi se kvalifikovali kao pouzdani
                pružaoci usluga za MSP.
              </td>
              <td className={getClassname([AICategory.aware])}>
                Sposobni su da pruže pouzdane usluge za MSP u manje složenim ili osnovnim scenarijima i
                situacijama.
              </td>
              <td className={getClassname([AICategory.ready])}>
                Demonstriraju sposobnost da budu pouzdani pružaoci usluga u kompleksnijim slučajevima, ali
                možda ne u svim oblastima ili domenima.
              </td>
              <td className={getClassname([AICategory.competent])}>
                Visoka stručnost i pouzdanost u pružanju usluga za MSPu svim mogućim situacijama, pokrivajući
                širok spektar potreba i izazova.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className={styles.section}>
        <Typography variant="title6" className={styles.sectionTitle}>
          SPMIND komponente
        </Typography>
        <Typography variant="description3" className={styles.secondTitle}>
          SPMIND se sastoji od 6 stubova, pri čemu svaki stub sadrži zahteve za procenu da li pružalac rešenja
          ima zadovoljavajući nivo zrelosti u oblasti AI.
        </Typography>
        <Typography variant="description3" className={styles.sectionDescription}>
          Organizaciona zrelost i korporativno upravljanje procenjuju da li je organizacija uspostavila sistem
          organizacije i upravljanja koji omogućava uspeh AI projekata. Komercijalna zrelost ispituje
          sposobnost organizacije u vezi sa komercijalizacijom i marketingom njihovog AI rešenja, pri čemu
          veštine nisu ograničene samo na dizajn sistema. Zrelost podataka proverava da li organizacija razume
          i kontroliše podatke i da li je svesna povezanih obaveza i problema. Etička zrelost procenjuje da li
          organizacija ima dovoljno razumevanja i kontrole za implementaciju AI sa kontrolisanim uticajem.
          Zrelost infrastrukture potvrđuje da se organizacija može osloniti na hardversku i softversku
          infrastrukturu kako bi osigurala tehnološku zrelost i implementaciju rešenja. Konačno, zrelost od
          početka do kraja proverava da li organizacija pokazuje znanje i ovladavanje svim fazama inženjeringa
          sistema, i odgovarajuće razmatranje ciljnog operativnog konteksta.
        </Typography>
        <Typography variant="description3" className={styles.secondDescription}>
          Zajedno, 6 SPMIND stubova pružaju kompletnu procenu kvalifikacija organizacije da postane pouzdan
          pružalac AI usluga.
        </Typography>
        <div className={styles.imageContainer}>
          <img src={diagram} alt="diagram" width={400} height={400} />
          <div className={styles.textContainer}>
            <Typography variant="description3">
              Šest stubova i 36 dimenzija procenjuju specifičnu oblast koja doprinosi ukupnoj zrelosti
              pružaoca rešenja.
            </Typography>
            <Typography variant="description3">
              U svakoj temi postoje različite dimenzije i svaka dimenzija se procenjuje na četiri nivoa
              zrelosti pružaoca rešenja:
            </Typography>
            <Typography variant="description3">Nekvalifikovan - Potrebno je napredovanje</Typography>
            <Typography variant="description3">Kvalifikovan - Istraživački nivo</Typography>
            <Typography variant="description3">Kvalifikovan - Srednji nivo</Typography>
            <Typography variant="description3">Kvalifikovan - Napredni nivo</Typography>
          </div>
        </div>
        <Typography variant="description3">
          Rezultati svake pojedinačne dimenzije se sabiraju i zatim izračunava njihov prosek kako bi se
          formirao konsolidovani rezultat za svaki stub.
        </Typography>
      </div>

      <div className={styles.section}>
        <table>
          <tbody>
            <tr>
              <td>Stubovi</td>
              <td>Dimenzije</td>
              <td>Procena</td>
            </tr>
            <tr>
              <td rowSpan={4}>Organizaciona zrelost i korporativno upravljanje</td>
              <td>Razumevanje lanca vrednosti</td>
              <td>
                Organizacija može identifikovati faze u lancu vrednosti u koje će se njena ponuda uklopiti.
                Obim lanca vrednosti i priroda njegovih faza zavisiće od specifičnog konteksta aplikanta (B2B
                ili B2C, itd.). Lanac vrednosti mora pokrivati minimalno moguće krajnje korisnike, kupce
                rešenja i podizvođače.
              </td>
            </tr>
            <tr>
              <td>Kultura donosioca odluka o AI</td>
              <td>
                Rukovodstvo i upravljanje kompanije koje se bavi AI projektima poseduje kulturu veštačke
                inteligencije i dovoljno razumevanje potreba, ograničenja, limita i rizika koji su povezani sa
                AI projektima
              </td>
            </tr>
            <tr>
              <td>Veštine tima uključenog u AI projekte</td>
              <td>
                Članovi tima uključenih u sve inženjerske faze neophodne za razvoj i implementaciju AI
                (uključujući softverske i podatkovne aspekte) su identifikovani, a kompanija primenjuje metode
                da osigura njihove veštine.
              </td>
            </tr>
            <tr>
              <td>Upravljanje zavisnostima i podizvođačkim odnosima</td>
              <td>
                Organizacija temeljno procenjuje i analizira ključne digitalne zavisnosti kao što su
                softverski alati, biblioteke i podaci, kao i odnose sa podizvođačima, kako bi se utvrdio
                njihov uticaj na AI projekte. To uključuje razmatranje potpunih, delimičnih ili manjinskih
                zavisnosti. Takođe, organizacija razvija i implementira proaktivne strategije za reagovanje i
                remedijaciju u slučaju prekida ili problema u ovim zavisnostima i podizvođačkim odnosima,
                osiguravajući time kontinuitet i stabilnost AI projekata.
              </td>
            </tr>
            <tr>
              <td rowSpan={8}>Spremnost u pogledu etike i upravljanja</td>
              <td>Savladavanje poslovnih case-study -a</td>
              <td>
                Organizacija je sposobna jasno da predstavi različite načine upotrebe (oblast delatnosti,
                specifična tehnološka očekivanja, veličina klijenta, itd.) za koje namenjuje svoja AI rešenja.
                Ove primene mogu se odnositi kako na spoljne kupce, tako i na sagovornike unutar same
                kompanije.
              </td>
            </tr>
            <tr>
              <td>Doslednost poslovnih strategija</td>
              <td>
                Organizacija primenjuje poslovnu strategiju koja je usklađena sa prirodom predloženog AI
                rešenja. Ako argument &ldquo; veštačke inteligencije &rdquo; nije istaknut u njihovoj
                marketinškoj strategiji, potrebno je objasniti relevantnost ovog izbora.
              </td>
            </tr>
            <tr></tr>
            <tr>
              <td>Relevantnost strategija razvoja i implementacije</td>
              <td>
                Organizacija primenjuje strategije razvoja i implementacije koje su prilagođene primenama za
                koje su namenjeni njeni proizvodi/usluge. Ovo uključuje razmatranje tipa klijenta (mala i
                srednja preduzeća, velike kompanije, itd.), kritičnosti sektora i strukture samih
                proizvoda/usluga.
              </td>
            </tr>
            <tr>
              <td>Sinergija sa ekosistemom</td>
              <td>
                Organizacija održava veze sa relevantnim ekosistemom, uključujući saradnju sa inkubatorima,
                klasterima konkurentnosti, učešćem u projektima, naučnim doprinosima i sličnim aktivnostima.
              </td>
            </tr>
            <tr>
              <td>Administrativna i poreska usaglašenost</td>
              <td>
                Organizacija mora biti registrovana u registru trgovine i kompanija te mora imati izmirene sve
                poreske i doprinose za socijalno osiguranje na dan podnošenja prijave.
              </td>
            </tr>
            <tr>
              <td>Iskustvo plasmana na tržište</td>
              <td>
                Organizacija sprovodi aktivnosti koje se odnose na plasiranje AI rešenja na tržište, kao što
                su aktivnosti koje se obavljaju sa kupcima, aktivnosti koje se odnose na regulatornu
                usklađenost u vezi sa krajnjom primenom AI rešenja, aktivnosti prodajnog odeljenja, itd.
              </td>
            </tr>
            <tr>
              <td>Reference kupaca</td>
              <td>
                Organizacija ima sposobnost da pruži referentne informacije o svojim klijentima: potvrde o
                uspehu prodajnih akcija ili integraciji AI rešenja sa klijentima, ili reference iz dokaza
                koncepta (POC) ako organizacija još nije izvršila prodajnu akciju.
              </td>
            </tr>
            <tr>
              <td rowSpan={7}>Zrelost u oblasti podataka</td>
              <td>Upravljanje podacima</td>
              <td>
                Organizacija sprovodi sistematski proces koji omogućava precizno identifikovanje različitih
                skupova podataka korišćenih kao ulaz i generisanih kao izlaz, prateći njihovo poreklo i
                određujući da li se tim podacima upravlja unutar organizacije ili van nje. Takođe, proces
                identifikuje odgovorne osobe koje su zadužene za obradu ovih podataka. Ova sveobuhvatna praksa
                omogućava efikasno upravljanje podacima unutar organizacije.
              </td>
            </tr>
            <tr>
              <td>Vladanje ciklusom podataka</td>
              <td>
                Organizacija je sposobna da za svaku fazu ciklusa podataka objasni metode korišćene za
                identifikaciju potreba i ograničenja povezanih sa svakom fazom. Ove faze treba da obuhvataju
                sve obrade i operacije koje se sprovode nad podacima od njihovog nastanka do brisanja.
              </td>
            </tr>
            <tr>
              <td>Pravičnost i zakonitost obrade podataka</td>
              <td>
                Organizacija primenjuje metode koje omogućavaju identifikaciju podataka i metapodataka koji
                potencijalno mogu uzrokovati nepravičnost u obradi sprovedenoj od strane veštačke
                inteligencije (AI), kao i elemenata čija obrada možda neće biti u skladu sa regulativama,
                zavisno o slučajevima primene.
              </td>
            </tr>
            <tr>
              <td>Vladanje pravima pristupa</td>
              <td>
                Organizacija primenjuje metode za ograničavanje pristupa podacima od strane modela koji bi
                mogli uzrokovati nepravičnu ili nezakonitu obradu, kako tokom faze razvoja, tako i u
                operativnim uslovima.
              </td>
            </tr>
            <tr>
              <td>Vladanje kvalitetom</td>
              <td>
                Organizacija sprovodi procese praćenja kvaliteta podataka tokom celog ciklusa podataka.
                Organizacija predlaže atribute kvaliteta podataka koji su relevantni za njen kontekst.
              </td>
            </tr>
            <tr>
              <td>Vladanje pripremom korpusa podataka</td>
              <td>
                U okviru procesa učenja modela, organizacija primenjuje metode za deljenje skupova podataka u
                skladu sa najboljim praksama u oblasti (primenljivo samo na određene vrste AI rešenja).
              </td>
            </tr>
            <tr>
              <td>Vladanje podacima u operativnim uslovima</td>
              <td>
                Organizacija primenjuje pristup zasnovan na riziku, koji obuhvata posebno rizike specifične za
                upotrebu veštačke inteligencije, a takođe uključuje i rizike po osnovna prava (uticaj na
                društvo i pojedince). (Ocena se vrši samo na sposobnost organizacije da identifikuje rizike, a
                ne na kvalitet i odgovarajućost strategija za smanjenje rizika).
              </td>
            </tr>
            <tr>
              <td rowSpan={7}>Zrelost u pogledu etike</td>
              <td>Svest o etici</td>
              <td>
                Organizacija sprovodi aktivnosti relevantne za podizanje svesti o etici među unutar tima
                uključenog u AI projekte.
              </td>
            </tr>
            <tr>
              <td>Upravljanje rizicima specifičnim za veštačku inteligenciju</td>
              <td>
                Organizacija primenjuje pristup zasnovan na riziku, koji obuhvata posebno rizike specifične za
                upotrebu veštačke inteligencije, a takođe uključuje i rizike po osnovna prava (uticaj na
                društvo i pojedince). (Ocena se vrši samo na sposobnost organizacije da identifikuje rizike, a
                ne na kvalitet i odgovarajućost strategija za smanjenje rizika).
              </td>
            </tr>
            <tr>
              <td>Osiguranje transparentnosti i razumljivosti</td>
              <td>
                Organizacija primenjuje metode kako bi garantovala da krajnji korisnici i osobe koje su
                direktno zahvaćene obradom koju obavlja AI rešenje budu adekvatno informisani o faktorima koji
                su uticali na generisanje izlaza sistema. Ove informacije su prilagođene tako da budu
                razumljive i korisne za njihove potrebe i svakodnevnu upotrebu.
              </td>
            </tr>
            <tr>
              <td>Osiguravanje transparentnosti procesa donošenja odluka</td>
              <td>
                Organizacija primenjuje metode kako bi pažljivo pratila podatke koji su doveli do izlaza iz AI
                rešenja. Organizacija je obavezna najmanje pokazati da primenjuje strategije za očuvanje
                sveobuhvatnog loga obrade koji je obavljen tokom procesa donošenja odluka.
              </td>
            </tr>
            <tr>
              <td>Transparentnost</td>
              <td>
                Organizacija pruža tehničku dokumentaciju koja posebno specifikuje vrste algoritama koji su
                implementirani, očekivane performanse i oblast primene (interna upotreba ili za određene
                saradnike organizacije).
              </td>
            </tr>
            <tr>
              <td>Informacije za relevantne strane</td>
              <td>
                Organizacija implementira sveobuhvatne strategije koje osiguravaju da relevantne strane,
                uključujući timove za razvoj, korisnike, inspektore i širu javnost, imaju pristup ključnim
                informacijama neophodnim za izgradnju i održavanje njihovih odnosa sa AI rešenjem.
              </td>
            </tr>
            <tr>
              <td>Uticaj na okolinu</td>
              <td>
                Organizacija primenjuje strategije kako bi ograničila uticaj svojih rešenja na okolinu. Ove
                strategije se mogu odnositi na podatke, algoritme ili interna procesa.
              </td>
            </tr>
            <tr>
              <td rowSpan={4}>Zrelost infrastrukture</td>
              <td>Kontrola infrastrukture podataka</td>
              <td>
                Organizacija formalizuje specifikacije infrastrukture potrebne za svaku fazu ciklusa podataka
                i osigurava implementaciju resursa (materijalnih i ljudskih) neophodnih za pravilno
                funkcionisanje infrastrukture u svakoj fazi (ove faze obuhvataju sve obrade i operacije koje
                se sprovode nad podacima od njihovog nastanka do brisanja).
              </td>
            </tr>
            <tr>
              <td>Izvrsnost infrastrukture za sistemsko inženjering</td>
              <td>
                Organizacija pažljivo definiše specifikacije infrastrukture potrebne u svakoj fazi ciklusa
                sistemskog inženjeringa i osigurava implementaciju neophodnih resursa, kako materijalnih tako
                i ljudskih, kako bi se osiguralo besprekorno funkcionisanje infrastrukture tokom svake faze.
                Ove faze obuhvataju ključne korake kao što su specifikacija, razvoj, verifikacija i
                validacija, implementacija i održavanje.
              </td>
            </tr>
            <tr>
              <td>Kontrola infrastrukture u operativnim uslovim</td>
              <td>
                Organizacija formalizuje specifikacije potrebne infrastrukture u operativnim uslovima i ili
                prilagođava svoj sistem infrastrukturi klijenta ili obaveštava svog klijenta na odgovarajući
                način o resursima (materijalnim i ljudskim) potrebnim za nesmetan rad sistema.
              </td>
            </tr>
            <tr>
              <td>Zaštita i sigurnost</td>
              <td>
                Organizacija primenjuje strategije za zaštitu podataka u svakoj fazi ciklusa sistemskog
                inženjeringa, na primer, politiku usaglašenosti sa GDPR-om.
              </td>
            </tr>
            <tr>
              <td rowSpan={6}>Sveobuhvatna zrelost</td>
              <td>Kontrola dizajna</td>
              <td>
                Organizacija formalizuje osnovne principe svojih AI rešenja uzimajući u obzir potrebe koje se
                odnose na ciljni operativni kontekst (vrstu korisnika, poslovne ograničenosti itd.) i tehničke
                ograničenosti.
              </td>
            </tr>
            <tr>
              <td>Izlaznost u inženjeringu podataka</td>
              <td>
                Organizacija formalizuje neophodne operacije u okviru faza inženjeringa podataka i uspostavlja
                odgovarajuće resurse (softver, hardver i tim).
              </td>
            </tr>
            <tr>
              <td>Vladanje razvojem modela</td>
              <td>
                Organizacija formalizuje neophodne operacije u okviru selekcije i razvoja AI modela i
                uspostavlja odgovarajuće resurse (softver, hardver i osoblje).
              </td>
            </tr>
            <tr>
              <td>Vladanje implementacijom sistema</td>
              <td>
                Organizacija formalizuje operacije neophodne za implementaciju kompletnog sistema i
                uspostavlja odgovarajuće resurse (softver, hardver i osoblje).
              </td>
            </tr>
            <tr>
              <td>Stručnost u verifikaciji</td>
              <td>
                Organizacija primenjuje strukturiran pristup za sprovođenje verifikacija AI rešenja. Metode
                verifikacije idu dalje od testiranja jedinica za pravilno funkcionisanje, obuhvatajući
                celokupna AI rešenja, a ne samo modele, i korišćeni pokazatelji performansi su prilagođeni
                vrsti performansi koje traži klijent.
              </td>
            </tr>
            <tr>
              <td>Kontrola implementacije</td>
              <td>
                Organizacija primenjuje procedure kako bi osigurala pravilnu upotrebu sistema, nadzor sistema
                nakon implementacije i omogućila izvođenje operacija održavanja.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  ) : (
    <div className={styles.loader}>
      <Skeleton height={300} width={'100%'} variant="rectangular" />
      <Skeleton height={150} width={'100%'} variant="rectangular" />
    </div>
  )
}

export default AiResults
